import { InjectionToken } from '@angular/core';
import { environment } from '@ups/xplat/core';
import {
  describeRynReplayReadFailure,
  describeRynReplayReadNoToken,
} from './ryn-replay-read-failure';
import { RynReplayAccessToken } from './ryn-replay-access-token';
import {
  IRynReplayStream,
  createEmptyRynReplayStream,
  describeRynReplayPlayability,
  mergeRynReplayStreams,
  parseRynReplayStream,
  rynReplayPlaybackItemCount,
} from './ryn-replay-stream';

/** Batches fetched per follow-up request after the opening batch. */
const RYN_REPLAY_BATCH_CHUNK = 8;

/**
 * Statuses the read API returns for a transient condition it explicitly marks as
 * worth retrying. The events endpoint maps a momentary dependency outage (Entra
 * token validation, the Software Security feature check, the SQL session index, or
 * blob storage) to 503, and its pipeline comment is explicit that "503 and not 500"
 * is chosen precisely so the caller knows retrying is worthwhile. 502 and 504 are
 * the gateway-level equivalents an Azure Functions cold start or worker restart can
 * surface. Throwing on the first one is the client behavior that filed WI 30793 (a
 * single-second 503 surfaced to a viewer as a NetworkFailure).
 */
const RYN_REPLAY_RETRYABLE_STATUSES = new Set([502, 503, 504]);

/** Total attempts (initial + retries) before a transient status is surfaced. */
const RYN_REPLAY_MAX_ATTEMPTS = 3;

/**
 * Backoff before each retry, indexed by the retry number (0-based). Short and
 * bounded: the point is to ride out a momentary blip, not to mask a real outage,
 * so the worst-case added latency before the honest "temporarily unavailable"
 * message stays under a second.
 */
const RYN_REPLAY_RETRY_BACKOFF_MS = [250, 750];

export interface IRynReplayProgressiveHandlers {
  /**
   * Fired once the accumulated stream is playable (has a FullSnapshot).
   * May return a Promise — the loader awaits it before fetching the tail so
   * early `onEventsExtended` chunks are not dropped while the replayer mounts.
   */
  onPlayable(stream: IRynReplayStream): void | Promise<void>;
  /** Fired for each follow-up chunk with the new rrweb events only. */
  onEventsExtended(stream: IRynReplayStream, newEvents: unknown[]): void;
}

/**
 * Where the player gets a session's events from (#30072 player).
 *
 * An interface rather than a direct fetch, because there are two legitimate
 * sources and they are not dev-versus-prod:
 *
 * - {@link RynReplayHttpEventSource} reads the gated read API. This is the
 *   normal path, the one a replay link from a Halo ITSM ticket opens.
 * - {@link RynReplayFileEventSource} opens a `.ndjson` export from disk. This
 *   is NOT a test double. A support engineer working an escalation is regularly
 *   handed an export by email, and a session whose blobs have aged out of
 *   retention can still be replayed from a copy someone kept. The vendor
 *   products we are replacing charge for API access to your own session data;
 *   being able to hold a session as a file is part of owning it outright.
 */
export interface IRynReplayEventSource {
  load(sessionId: string): Promise<IRynReplayStream>;
  loadProgressive?(
    sessionId: string,
    handlers: IRynReplayProgressiveHandlers
  ): Promise<IRynReplayStream>;
}

export const RYN_REPLAY_EVENT_SOURCE =
  new InjectionToken<IRynReplayEventSource>('RYN_REPLAY_EVENT_SOURCE');

/** Thrown with a message already fit to show a user. */
export class RynReplayLoadError extends Error {}

/**
 * Total batch count from the read API. Returns undefined when the header is
 * absent — common cross-origin when Access-Control-Expose-Headers omits the
 * X-Replay-* names. Callers must not treat undefined as 1 batch.
 */
const readBatchCount = (response: Response): number | undefined => {
  const raw = response.headers.get('X-Replay-Batch-Count');
  if (!raw) {
    return undefined;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

/** Session index row fields exposed on the events response headers. */
const readSessionAttributionFromResponse = (
  response: Response
): IRynReplayStream['sessionAttribution'] => {
  const securityUserId = response.headers
    .get('X-Replay-Session-Security-User-Id')
    ?.trim();
  const startedAtRaw = response.headers
    .get('X-Replay-Session-Started-At')
    ?.trim();
  const startedAtMs = startedAtRaw ? Date.parse(startedAtRaw) : Number.NaN;
  if (!securityUserId && !Number.isFinite(startedAtMs)) {
    return undefined;
  }
  return {
    ...(securityUserId ? { securityUserId } : {}),
    ...(Number.isFinite(startedAtMs) ? { startedAtMs } : {}),
  };
};

/**
 * Reads `GET {readApiUrl}/sessions/{sessionId}/events`.
 *
 * The token is supplied by the host app rather than acquired here: apps in this
 * monorepo differ on whether the viewer's bearer comes from Auth0 or Entra, and
 * the read API validates an ENTRA token specifically. Making the caller pass it
 * keeps this class out of the business of guessing which one it is holding.
 */
export class RynReplayHttpEventSource implements IRynReplayEventSource {
  constructor(
    private readonly accessToken: () => Promise<RynReplayAccessToken>,
    private readonly fetchRef: typeof fetch = globalThis.fetch?.bind(
      globalThis
    ),
    // Injectable so retry tests do not wait the real backoff. Defaults to a real
    // sleep; nothing in production passes it.
    private readonly sleep: (ms: number) => Promise<void> = (ms) =>
      new Promise((resolve) => setTimeout(resolve, ms))
  ) {}

  async load(sessionId: string): Promise<IRynReplayStream> {
    return this.loadProgressive(sessionId, {
      onPlayable: () => undefined,
      onEventsExtended: () => undefined,
    });
  }

  async loadProgressive(
    sessionId: string,
    handlers: IRynReplayProgressiveHandlers
  ): Promise<IRynReplayStream> {
    const base = environment.rynReplay?.readApiUrl?.replace(/\/+$/, '');
    if (!base) {
      throw new RynReplayLoadError(
        'The replay read API is not configured for this app, so recordings cannot be loaded.'
      );
    }

    const access = await this.accessToken();
    if (!access.token) {
      throw new RynReplayLoadError(describeRynReplayReadNoToken(access.reason));
    }
    const token = access.token;

    let accumulated = createEmptyRynReplayStream();
    let totalBatches: number | undefined;
    let nextBatch = 0;
    let playableNotified = false;

    const fetchRange = async (fromBatch: number, toBatch: number) => {
      const url = new URL(
        `${base}/sessions/${encodeURIComponent(sessionId)}/events`
      );
      url.searchParams.set('fromBatch', String(fromBatch));
      url.searchParams.set('toBatch', String(toBatch));

      // A GET is idempotent, so a transient status is safe to retry a bounded
      // number of times before surfacing it. A terminal status (401/403/404) is
      // thrown on the first response: retrying only delays the accurate,
      // actionable message. See RYN_REPLAY_RETRYABLE_STATUSES for the set.
      for (let attempt = 1; ; attempt++) {
        // Do not set Accept-Encoding: the browser forbids that header name and
        // strips it; browsers already negotiate gzip on their own. Setting it
        // only misleads non-browser fetch doubles into sending a forbidden header.
        const response = await this.fetchRef(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const stream = parseRynReplayStream(await response.text());
          const attribution = readSessionAttributionFromResponse(response);
          return {
            response,
            stream: attribution
              ? { ...stream, sessionAttribution: attribution }
              : stream,
          };
        }

        if (
          !RYN_REPLAY_RETRYABLE_STATUSES.has(response.status) ||
          attempt >= RYN_REPLAY_MAX_ATTEMPTS
        ) {
          throw new RynReplayLoadError(
            describeRynReplayReadFailure(response.status)
          );
        }

        await this.sleep(
          RYN_REPLAY_RETRY_BACKOFF_MS[
            Math.min(attempt - 1, RYN_REPLAY_RETRY_BACKOFF_MS.length - 1)
          ]
        );
      }
    };

    while (
      describeRynReplayPlayability(accumulated) !== undefined &&
      (totalBatches === undefined || nextBatch < totalBatches)
    ) {
      const itemsBefore = rynReplayPlaybackItemCount(accumulated);
      const { response, stream } = await fetchRange(nextBatch, nextBatch);
      const headerCount = readBatchCount(response);
      if (headerCount !== undefined) {
        totalBatches = headerCount;
      }
      accumulated = mergeRynReplayStreams(accumulated, stream);
      nextBatch += 1;

      // Without X-Replay-Batch-Count (CORS), keep pulling single batches until
      // playable or a slice adds nothing playable (past the end / empty range).
      if (
        totalBatches === undefined &&
        rynReplayPlaybackItemCount(accumulated) === itemsBefore
      ) {
        break;
      }
    }

    if (
      !playableNotified &&
      describeRynReplayPlayability(accumulated) === undefined
    ) {
      // Await mount/import before the tail loop. Otherwise a fast next-page
      // fetch can fire onEventsExtended before the replayer exists and the
      // component silently drops that first tail chunk.
      await handlers.onPlayable(accumulated);
      playableNotified = true;
    }

    while (totalBatches === undefined || nextBatch < totalBatches) {
      const toBatch =
        totalBatches !== undefined
          ? Math.min(nextBatch + RYN_REPLAY_BATCH_CHUNK - 1, totalBatches - 1)
          : nextBatch + RYN_REPLAY_BATCH_CHUNK - 1;
      const { response, stream } = await fetchRange(nextBatch, toBatch);
      const headerCount = readBatchCount(response);
      if (headerCount !== undefined) {
        totalBatches = headerCount;
      }
      const previousCount = accumulated.events.length;
      const previousItems = rynReplayPlaybackItemCount(accumulated);
      accumulated = mergeRynReplayStreams(accumulated, stream);
      const newEvents = accumulated.events.slice(previousCount);
      // An idle page (console/network/heartbeat, no DOM mutations) can add
      // nothing playable while more batches remain. Only abandon when the
      // batch-count header is unknown (mirror the open-loop empty-slice guard);
      // when totalBatches is known, keep going until nextBatch reaches it so a
      // long idle stretch is not truncated and mislabeled complete.
      if (
        rynReplayPlaybackItemCount(accumulated) === previousItems &&
        totalBatches === undefined
      ) {
        break;
      }
      handlers.onEventsExtended(accumulated, newEvents);
      nextBatch = toBatch + 1;
      if (totalBatches !== undefined && nextBatch >= totalBatches) {
        break;
      }
    }

    const batchesDelivered = totalBatches ?? nextBatch;
    if (accumulated.completeness.status !== 'truncated') {
      accumulated = {
        ...accumulated,
        completeness: {
          status: 'complete',
          batchesWritten: batchesDelivered,
          batchesExpected: batchesDelivered,
          batchesSkipped: 0,
        },
      };
    }

    return accumulated;
  }
}

/**
 * Opens a `.ndjson` export. The wire format and the file format are the same
 * bytes by design, so this parses through exactly the same code path as the
 * HTTP source and cannot drift from it.
 */
export class RynReplayFileEventSource implements IRynReplayEventSource {
  constructor(private readonly file: Blob) {}

  async load(): Promise<IRynReplayStream> {
    return parseRynReplayStream(await this.file.text());
  }

  async loadProgressive(
    _sessionId: string,
    handlers: IRynReplayProgressiveHandlers
  ): Promise<IRynReplayStream> {
    const stream = await this.load();
    if (describeRynReplayPlayability(stream) === undefined) {
      await handlers.onPlayable(stream);
    }
    return stream;
  }
}
